import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
const _excluded = ["ref"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { deepEqual } from "fast-equals";
import memoize from "micro-memoize";
import STRINGS from "../Table/strings.js";
import { pxToRem } from "../themes/common/utilities";
import { BOX_TSHIRT_NAMES, BOX_TSHIRT_SIZES, DEFAULT_GRID_SPACING, DISABLED, FOCUSABLE_SELECTORS, HIDDEN } from "./constants";
/** Used to generate unique IDs. */

const idCounter = {};
const hasOwnProperty = Object.prototype.hasOwnProperty;
const toString = Object.prototype.toString;
const objectProto = Object.prototype;
/**
 * Memoize function calls with arguments that are
 * complex objects (hence "deep"). Do not use for
 * standard types.
 * @param {Function} fct - the function to optimize.
 * @returns the optimized function.
 */

export function memoizeDeep(fct) {
  return memoize(fct, {
    isEqual: deepEqual
  });
}
/**
 * Returns row key text based on the `itemKey` and `row`.
 * @param {itemKey} - item key value.
 * @param {row} - row string value.
 */

export function getRowKeyText(itemKey) {
  let row = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : STRINGS.row;
  return `${itemKey} ${row}`;
}
/**
 * Gets a DOM element, and returns it if it's focusable.
 * Otherwise, it returns null
 * @param {*} el Element to check if it's focusable
 */

export function filterFocusableItems(el) {
  const style = el.style;
  const tabindex = el.getAttribute("tabindex");
  const focusable = tabindex === null || tabindex !== "-1";

  if (!el.hasAttribute(DISABLED) && !el.hasAttribute(HIDDEN) && style.display !== "none" && style.visibility !== HIDDEN && style.opacity !== "0" && focusable) {
    return el;
  }

  return null;
}
/**
 * Gets a DOM element, and returns a collection of all its
 * focusable child elements.
 * @param {Element} domNode Container element to get its focusable elements.
 * @param {String} [customSelector=FOCUSABLE_SELECTORS] - A selector to use
 *  to find the elements that may be focusable.
 */

export function getFocusableElements(domNode) {
  let customSelector = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : FOCUSABLE_SELECTORS;
  let focusableElements = Array.prototype.slice.call(domNode.querySelectorAll(customSelector));
  focusableElements = focusableElements.filter(filterFocusableItems);
  return focusableElements;
}
/**
 * Determine if an active element should be focusable.
 */

export function isActiveElementFocusable() {
  const activeElement = document.activeElement;
  let activeElementTagSelector = activeElement && activeElement.tagName.toLowerCase();

  if (activeElement && (activeElementTagSelector === "a" || activeElementTagSelector === "area") && activeElement.href) {
    activeElementTagSelector = `${activeElementTagSelector}[href]`;
  }

  return FOCUSABLE_SELECTORS.split(", ").indexOf(activeElementTagSelector) !== -1;
}
/**
 * A constant set to `true` if the current device is an `iOS` device,
 * otherwise set to `false`
 */

export const isIOS = typeof document.body.style.webkitOverflowScrolling !== "undefined";
/**
 * Checks if `value` is a valid array-like length.
 */

function isLength(value) {
  return typeof value === "number" && value > -1 && value % 1 === 0 && value <= 9007199254740991;
}
/**
 * Checks if `value` is likely a prototype object.
 */


function isPrototype(value) {
  const Ctor = value && value.constructor;
  const proto = typeof Ctor === "function" && Ctor.prototype || objectProto;
  return value === proto;
}
/**
 * Checks if `value` is an empty object, collection, map, or set.
 * Objects are considered empty if they have no own enumerable
 * string keyed properties.
 */


export function isEmpty(value) {
  if (value === null || typeof value === "undefined") {
    return true;
  }

  if (typeof value !== "function" && isLength(value.length) && (Array.isArray(value) || typeof value === "string" || typeof value.splice === "function")) {
    return !value.length;
  }

  const tag = toString.call(value);

  if (tag === "[object Map]" || tag === "[object Set]") {
    return !value.size;
  }

  if (isPrototype(value)) {
    return !Object.keys(value).length;
  }

  for (const key in value) {
    /* istanbul ignore next */
    if (hasOwnProperty.call(value, key)) {
      return false;
    }
  }

  return true;
}
/**
 * Checks if `value` is a plain object, that is, an object created by the
 * `Object` constructor or one with a `[[Prototype]]` of `null`.
 */

export function isPlainObject(obj) {
  return obj !== null && typeof obj === "object" && obj.constructor === Object;
}
/**
 * Merges 2 objects together (deep, any levels)
 */

export function safeMerge(target, source) {
  let options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {
    clone: true
  };
  const output = options.clone ? _objectSpread({}, target) : target;

  if (isPlainObject(target) && isPlainObject(source)) {
    Object.keys(source).forEach(key => {
      if (key === "__proto__") {
        return;
      }

      if (isPlainObject(source[key]) && key in target && isPlainObject(target[key])) {
        output[key] = safeMerge(target[key], source[key], options);
      } else {
        output[key] = source[key];
      }
    });
  }

  return output;
}
/**
 * Merges 2 objects together (deep, any levels, but it modifies the parameters)
 * It's the same as safeMerge, but without cloning so it's faster but it's
 * destructive.
 */

export function fastMerge(target, source) {
  if (!source) {
    return target;
  }

  return safeMerge(target, source, {
    clone: false
  });
}
/**
 * React utility to merge refs.
 * When developing low level UI components, it is common to have to use a local
 * ref but also support an external one using React.forwardRef. Natively, React
 * does not offer a way to set two refs inside the ref property.
 * @param Array of refs (object, function, etc.)
 *
 * @example
 *
 * import React from "react";
 * import mergeRefs from "react-merge-refs";
 *
 * const Example = React.forwardRef(function Example(props, ref) {
 *   const localRef = React.useRef();
 *   return <div ref={mergeRefs([localRef, ref])} />;
 * });
 *
 */

export function mergeRefs(refs) {
  return value => {
    refs.forEach(ref => {
      if (typeof ref === "function") {
        ref(value);
      } else if (ref !== null) {
        ref.current = value;
      }
    });
  };
}
/**
 * Register Form field.
 */

export function registerField(_ref) {
  let {
    register,
    name,
    required,
    requiredErrorMessage = "Field is required.",
    customValidators,
    handleBlur,
    handleChange
  } = _ref;

  if (register) {
    const validate = {};
    const registerOptions = {
      required: required ? requiredErrorMessage : false
    };
    /**
     * Loop through all customValidators and adding them
     * to the react-hook-form register options.
     */

    if (customValidators) {
      customValidators.forEach((validator, idx) => {
        validate[idx] = validator;
      });
      registerOptions.validate = validate;
    }
    /**
     * Adding our custom on blur event handler to the
     * react-hook-form register options.
     */


    registerOptions.onBlur = handleBlur;
    /**
     * Adding our custom on change event handler to the
     * react-hook-form register options.
     */

    registerOptions.onChange = handleChange;
    /**
     * And finally, actually registering this field with
     * the options we set above.
     */

    const _register = register(name, registerOptions),
          {
      ref
    } = _register,
          reactHookFormRest = _objectWithoutProperties(_register, _excluded);

    return {
      inputRef: ref,
      reactHookFormRest
    };
  }

  return {
    reactHookFormRest: {
      onBlur: handleBlur,
      onChange: handleChange
    }
  };
}
/*
 * If the value of the named propertyOrFunction is a function then invoke it
 * with the argument and context provided; otherwise, return it.
 * @param  {Function|String|JSX} propertyOrFunction A value or a function
 * @param  {Object} [arg={}]     Arguments object to pass to propertyOrFunction
 *                               if invoked.
 * @param  {Any} [context=null]  Arguments object to pass to propertyOrFunction
 *                               if invoked.
 * @return {String|JSX}          Return `propertyOrFunction` if it is not a
 *                               function, or the result of the execution of
 *                               `propertyOrFunction` with arg and context
 *                               applied.
 */

export function resultOf(propertyOrFunction) {
  let arg = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  let context = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
  return typeof propertyOrFunction !== "function" ? propertyOrFunction : propertyOrFunction.call(context, arg);
}
/**
 * Utility function that converts the "spacing" prop values into an
 * object that can be used as a css prop to add paddings. Please refer
 * to the "Spacing" section of the API doc under Customization.
 *
 * This function is primarily used by the Box component, but it can
 * be re-used in case we need to convert the spacing prop.
 *
 * @param {Object} spacing Custom spacing for the component.
 * @returns {Object}       Returns an object with paddings that correspond
 *                         to the spacing.
 */

export function getBoxPaddings(spacing) {
  let paddings = {};

  if (typeof spacing === "number") {
    paddings = {
      padding: pxToRem(spacing * DEFAULT_GRID_SPACING)
    };
  } else if (typeof spacing === "string") {
    paddings = {
      padding: pxToRem(BOX_TSHIRT_SIZES[spacing] * DEFAULT_GRID_SPACING)
    };
  } else {
    paddings = !isEmpty(spacing) ? Object.keys(spacing).map(prop => {
      const value = typeof spacing[prop] === "string" ? BOX_TSHIRT_SIZES[spacing[prop]] : spacing[prop];
      return {
        [BOX_TSHIRT_NAMES[prop]]: pxToRem(value * DEFAULT_GRID_SPACING)
      };
    }).reduce(fastMerge, {}) : paddings;
  }

  return paddings;
}
/**
 * Generate a random number to append to an `id` string.
 * Example: pnr-panel-4237412347184
 *
 * WARNING: Do not use this method directly in a functional
 * component, use the `useUniqueId` hook which is specifically
 * made for none class components and uses this method behind
 * the scenes.
 *
 * NOTE: a more predictable generator is used when not in
 * production, so that the results do not break
 * potential snapshot tests.
 *
 * @param {String} prefix - When a prefix is provided, the
 *    function will return a random number appended to
 *    the provided prefix.
 * Example: __uniqueID("pnr-checkbox-")
 * @returns {String} - Returns a string with random numbers.
 */

export function __uniqueID() {
  let prefix = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : "$pnr$";

  if (process.env.NODE_ENV !== "production") {
    if (!idCounter[prefix]) {
      idCounter[prefix] = 0;
    }

    const id = ++idCounter[prefix];

    if (prefix === "$pnr$") {
      return `${id}`;
    }

    return `${prefix}${id}`;
  }

  const randomNumber = `${Math.random()}`.slice(2);
  return `${prefix}${randomNumber}`;
}
/*
 * Get the values of given object. Polyfill for Object.values.
 * @return {Array}  Returns an array of values
 */

export function getObjectValues(obj) {
  const objValues = [];

  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      objValues.push(obj[key]);
    }
  }

  return objValues;
}
/**
 * Returns a function that returns the object's key value.
 * If `key` is not passed, assume id is the `key`.
 *
 * Usage:
 * // `key` as string
 * const item = {paymentId: 1, accountNumber: 123};
 * const getKey = getObjectKeyFn("paymentId");
 * console.log(getId(item)); // this will log 1
 *
 * // `key` as a function
 * const item = {accountNumber: 123, primaryKey: {id: 1}};
 * const getKey = getObjectKeyFn(item => item.primaryKey.id);
 * console.log(getId(item)); // this will log 123
 *
 * @param {String|Function} key
 * @param {String} keyLabel
 */

export function getObjectKeyFn(key) {
  let keyLabel = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : "itemKey";

  if (!key) {
    return item => {
      if (typeof item.id === "undefined" && process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.error(`Your item's data doesn't have an \`id\`. Provide one or specify the \`${keyLabel}\`.`);
      }

      return item.id;
    };
  }

  if (typeof key === "string") {
    return item => item && item[key];
  }

  return item => item && key(item);
}
/**
 * @description A utility function which serves as closest function
 * to return the closest element based on validation given.
 * @param {Element} el dom element.
 * @param {Function} fn to check validation.
 * @returns {Element} Returns closest element.
 */

export function closest(el, fn) {
  return el && (fn(el) ? el : closest(el.parentNode, fn));
}
/**
 * @see https://developer.mozilla.org/en-US/docs/Web/API/DataTransfer
 * @see https://developer.mozilla.org/en-US/docs/Web/Events/change
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Document/drop_event
 *
 * @param {Event} event A change event from a file input, or a drop event.
 * @return {DataTransferItem[]|File[]}
 */

export function getDataTransferItems(event) {
  let dataTransferItemsList = [];

  if (event.dataTransfer) {
    const dt = event.dataTransfer;

    if (dt.files && dt.files.length) {
      dataTransferItemsList = dt.files;
    } else if (dt.items && dt.items.length) {
      /*
       * During the drag event the dataTransfer.files is null
       * but Chrome implements some drag store, which is accessible via dataTransfer.items
       */
      dataTransferItemsList = dt.items;
    }
  } else if (event.target && event.target.files) {
    dataTransferItemsList = event.target.files;
  } // Convert from DataTransferItemsList to a native Array


  return Array.prototype.slice.call(dataTransferItemsList);
}
/**
 * Returns items text based on the data length.
 * It will be useful in data components examples where `FindBy`
 * is configured. E.g. Table, ModalSelect, Listview, ListBuilder
 * @param {resultsLength}  - data length.
 */

export function getItemsText(resultsLength) {
  let itemsText = "items";

  if (resultsLength === 1) {
    itemsText = "item";
  }

  return `${resultsLength} ${itemsText} found`;
}
/**
 * renderRequiredIcon returns true if the asterisk icon is to be rendered.
 * The asterisk icon denotes a required field in the CEO theme.
 * This function uses three parameters to determine if the icon should be rendered.
 * They are: required, noAsterisk, and showRequiredIcon.
 * This function can be used by all Form components to reduce complexity.
 *
 * @param {Boolean} required
 * @param {Boolean} noAsterisk
 * @param {Object} cssRoot
 * @returns Boolean whether to display the required icon or not.
 */

export function renderRequiredIcon(required, noAsterisk, showRequiredIcon) {
  return required && !noAsterisk && showRequiredIcon;
}